import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type NotificationType = 
  | "credit-deduction" 
  | "insufficient-credit" 
  | "success" 
  | "error" 
  | "info" 
  | "warning";

export interface Notification {
  id: string;
  type: NotificationType;
  data?: Record<string, any>;
  duration?: number;
}

interface NotificationContextType {
  notifications: Notification[];
  showCreditDeduction: (amount: number) => void;
  showInsufficientCredit: (currentCredits: number, requiredCredits: number) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

let notificationId = 0;
const generateId = () => `notification-${++notificationId}-${Date.now()}`;

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, "id">) => {
    const id = generateId();
    setNotifications(prev => [...prev, { ...notification, id }]);
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const showCreditDeduction = useCallback((amount: number) => {
    addNotification({
      type: "credit-deduction",
      data: { amount },
      duration: 5000,
    });
  }, [addNotification]);

  const showInsufficientCredit = useCallback((currentCredits: number, requiredCredits: number) => {
    addNotification({
      type: "insufficient-credit",
      data: { currentCredits, requiredCredits },
      duration: 8000,
    });
  }, [addNotification]);

  const showSuccess = useCallback((title: string, message?: string) => {
    addNotification({
      type: "success",
      data: { title, message },
      duration: 5000,
    });
  }, [addNotification]);

  const showError = useCallback((title: string, message?: string) => {
    addNotification({
      type: "error",
      data: { title, message },
      duration: 6000,
    });
  }, [addNotification]);

  const showInfo = useCallback((title: string, message?: string) => {
    addNotification({
      type: "info",
      data: { title, message },
      duration: 5000,
    });
  }, [addNotification]);

  const showWarning = useCallback((title: string, message?: string) => {
    addNotification({
      type: "warning",
      data: { title, message },
      duration: 6000,
    });
  }, [addNotification]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showCreditDeduction,
        showInsufficientCredit,
        showSuccess,
        showError,
        showInfo,
        showWarning,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};
